import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Dropdown } from 'semantic-ui-react';

import { IconButton } from '../Button/IconButton';
import { Layout } from '../../layout/Layout/Layout';
import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

const VIEWPORT_MARGIN = 8;

// `fixed` exists so ancestors with overflow:hidden cannot clip the menu, but a
// fixed box with auto offsets is painted at its *unscrolled* layout position.
// Inside a scrolled container (e.g. the ag-grid header viewport, which scrolls
// via scrollLeft) that lands the menu scrollLeft pixels to the right of its
// anchor, so `fixed` menus get measured viewport coordinates instead.
const calculateMenuPosition = ({
  anchorRect,
  menuWidth,
  direction,
}: {
  anchorRect: DOMRect;
  menuWidth: number;
  direction: 'left' | 'right';
}) => {
  const preferredLeft =
    direction === 'left' ? anchorRect.right - menuWidth : anchorRect.left;
  const furthestLeft = window.innerWidth - menuWidth - VIEWPORT_MARGIN;

  return {
    top: `${anchorRect.bottom}px`,
    left: `${Math.max(VIEWPORT_MARGIN, Math.min(preferredLeft, furthestLeft))}px`,
  };
};

export const ContextMenu = ({
  anchor,
  children,
  className,
  direction = 'right',
  fixed, // Use only if a parent element is chopping off the menu
  fullscreenOn,
  name,
  fluid,
}: {
  anchor?: React.ReactElement;
  children: React.ReactNode;
  className?: string;
  direction?: 'left' | 'right';
  fixed?: boolean;
  fullscreenOn?: 'smallScreen' | 'tablet' | 'mobile';
  name?: string;
  fluid?: boolean;
}) => {
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const frameRef = useRef<number | undefined>(undefined);
  /*
    Set while the focus is on its way from the trigger into the menu, which is the one reason to
    refuse a close. **Not cleared by reading it**: Semantic asks to close twice for a single blur -
    once from its own handler and once from the re-render that follows - so a one-shot refusal
    lets the second one through. Every blur rewrites it, which is the only clearing it needs: a
    press moves the focus before its click arrives, and Escape is answered without consulting it.
  */
  const focusEnteringMenuRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<React.CSSProperties>();

  const positionMenu = useCallback(() => {
    const dropdown = wrapperRef.current?.querySelector('.ui.dropdown');
    const menu = wrapperRef.current?.querySelector('.menu.visible');

    if (!dropdown || !menu) {
      return;
    }

    const nextPosition = calculateMenuPosition({
      anchorRect: dropdown.getBoundingClientRect(),
      menuWidth: menu.getBoundingClientRect().width,
      direction,
    });

    // Scrolling that does not move the anchor should not re-render the menu
    setMenuPosition(currentPosition =>
      currentPosition?.top === nextPosition.top &&
      currentPosition.left === nextPosition.left
        ? currentPosition
        : nextPosition
    );
  }, [direction]);

  const schedulePositionMenu = useCallback(() => {
    if (frameRef.current !== undefined) {
      return;
    }

    frameRef.current = window.requestAnimationFrame(() => {
      frameRef.current = undefined;
      positionMenu();
    });
  }, [positionMenu]);

  useLayoutEffect(() => {
    if (!fixed || !isOpen) {
      setMenuPosition(undefined);
      return;
    }

    positionMenu();
    // Capture phase so scrolling an inner container, not just the page, keeps
    // an open menu pinned to its anchor. Coalesced into a single frame because
    // ag-grid emits a scroll event per viewport it syncs.
    window.addEventListener('scroll', schedulePositionMenu, true);
    window.addEventListener('resize', schedulePositionMenu);

    // Once a scrolling ancestor has clipped the anchor away the menu points at
    // nothing, so close it rather than strand it against the viewport edge.
    const anchorElement = wrapperRef.current?.querySelector('.ui.dropdown');
    let anchorObserver: IntersectionObserver | null = null;
    if (anchorElement && typeof IntersectionObserver !== 'undefined') {
      anchorObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => !entry.isIntersecting)) {
          setIsOpen(false);
        }
      });
      anchorObserver.observe(anchorElement);
    }

    return () => {
      anchorObserver?.disconnect();
      window.removeEventListener('scroll', schedulePositionMenu, true);
      window.removeEventListener('resize', schedulePositionMenu);

      if (frameRef.current !== undefined) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = undefined;
      }
    };
  }, [fixed, isOpen, positionMenu, schedulePositionMenu]);

  return (
    <span
      ref={wrapperRef}
      /*
        Capture, so these land before Semantic's own handlers on the dropdown inside and the answer
        is ready by the time it asks to close.
      */
      onBlurCapture={event => {
        const menu = wrapperRef.current?.querySelector('.menu');

        focusEnteringMenuRef.current = !!(
          event.relatedTarget instanceof Node &&
          menu?.contains(event.relatedTarget)
        );
      }}
      /*
        **Escape is shut here rather than left to Semantic.** Its own handler is a `document`
        listener, which runs before React reaches this subtree, so the refusal below would still
        be standing when it asks - and a reader holding the focus inside the menu could not get
        out of it. Closing straight away rather than clearing the refusal first, because this is
        the only key that closes a menu built from children.
      */
      onKeyDownCapture={event => {
        if (event.key === 'Escape') setIsOpen(false);
      }}
      data-test={name ? `${name}-context-menu` : 'context-menu'}
      className={AttrsHelper.formatClassname(
        'context-menu',
        anchor && 'custom-anchor',
        fixed && 'fixed-menu',
        fullscreenOn && `fullscreen-${fullscreenOn}`
      )}
    >
      <Dropdown
        data-test={name}
        // semantic ships `.ui.dropdown > .left.menu { left: auto !important }`,
        // which would beat the measured offset, so a fixed menu never takes the
        // leftward class and aligns itself instead.
        direction={fixed ? 'right' : direction}
        floating
        fluid={fluid}
        inline={false}
        icon={null}
        className={className}
        /*
          Controlled whether or not it is `fixed`, so that closing is this component's decision -
          Semantic's own is made on the trigger's `focusout` and is wrong for a menu holding
          anything focusable.
        */
        open={isOpen}
        onOpen={() => {
          setIsOpen(true);
        }}
        onClose={() => {
          /*
            **The one close worth refusing is the one no reader asked for.** A control in the menu
            that takes focus on `mousedown` - a checkbox reaching its own input - blurs the trigger
            before the press has become a click, and Semantic reads that as the menu being
            abandoned. Closing then pulls the menu out from under the pointer and the click lands
            on whatever was behind it. Every other close is still honoured, so a menu item shuts
            the menu exactly as it did before.
          */
          if (focusEnteringMenuRef.current) return;

          setIsOpen(false);
        }}
        trigger={
          <Layout position="middle" name="context-menu-anchor">
            {anchor || (
              <IconButton
                pattern="secondary"
                variation="minimal"
                icon="vertical-ellipsis"
              />
            )}
          </Layout>
        }
        upward={false}
      >
        <Dropdown.Menu style={menuPosition}>{children}</Dropdown.Menu>
      </Dropdown>
    </span>
  );
};
